import { BASIC_COLOR_SET } from 'constants/chartConstants';

import Highcharts, { Options, Point } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useMemo } from 'react';

interface BarSeries {
  name: string;
  data: number[];
  color?: string;
}

interface LineData {
  name: string;
  data: number[];
  color?: string;
}

interface VerticalBarChartProps {
  categories: string[]; // x-axis categories
  barData: BarSeries[]; // Array of series for bar data
  isStaking?: boolean;
  lineData?: LineData; // Line series data
  title?: string;
  subtitle?: string;
  yAxisTitles?: {
    barAxisTitle: string;
    lineAxisTitle?: string;
  };
  max?: number; // Maximum value for bar y-axis
  tooltipFormatter?: (point: Highcharts.Point) => string | HTMLElement; // Custom tooltip formatter
  onBarClick?: (point: Point, index: number) => void;
  maxHeight?: number;
  highlightColor?: string; // Add highlight color prop
  changeColorOnBarClick?: boolean;
}

const VerticalBarChart = ({
  categories,
  barData,
  isStaking = false,
  title = 'Bar Chart',
  subtitle = '',
  yAxisTitles = { barAxisTitle: '', lineAxisTitle: '' },
  max,
  tooltipFormatter,
  lineData,
  onBarClick,
  highlightColor,
  changeColorOnBarClick,
}: VerticalBarChartProps): React.ReactElement => {
  const [selectedBarIndex, setSelectedBarIndex] = React.useState<number | null>(null);

  const chartOptions = useMemo(
    () =>
      ({
        chart: {
          type: 'column',
          height: 600,
          style: {
            fontFamily: 'Noto Sans, sans-serif',
            fontSize: 'clamp(12px, 14px, 1.5vw)',
          },
        },
        title: {
          text: title,
          style: {
            fontSize: 'clamp(12px, 14px, 1.5vw)',
          },
        },
        subtitle: {
          text: subtitle,
        },
        xAxis: {
          categories,
          labels: {
            style: {
              fontWeight: 'bold',
            },
          },
        },
        yAxis: [
          {
            // Primary y-axis for bars
            title: {
              text: yAxisTitles.barAxisTitle,
            },
            max,
            min: 0,
          },
          {
            // Secondary y-axis for line
            title: {
              text: yAxisTitles.lineAxisTitle,
            },
            opposite: true,
          },
        ],
        series: [
          // Bar series
          ...barData.map((series, index) => ({
            name: series.name,
            type: 'column',
            data: series.data,
            yAxis: 0, // Use primary y-axis
            color:
              selectedBarIndex === index ? highlightColor : series.color || BASIC_COLOR_SET[index], // Use passed color or fallback to default set
          })),
          // Line series
          lineData && {
            name: lineData.name,
            type: 'line',
            data: lineData.data,
            color: lineData.color || '#d425a3',
            yAxis: 1, // Use secondary y-axis
            lineWidth: 3,
          },
        ].filter(Boolean),
        plotOptions: {
          column: {
            stacking: isStaking ? 'normal' : null,
          },
        },
        point: {
          events: {
            click: function (this: Point) {
              const index = this.index;

              // Handle color toggling for bar highlighting
              if (changeColorOnBarClick) {
                setSelectedBarIndex((prevIndex) => (prevIndex === index ? null : index));
              }

              // Trigger custom callback if provided
              if (onBarClick) {
                onBarClick(this, index);
              }
            },
          },
        },
        tooltip: {
          shared: false,
          useHTML: true,
          formatter: function () {
            if (tooltipFormatter) {
              return tooltipFormatter(this.point);
            }
            return `<b>${this.x}</b><br/>${this.series.name}: ${this.y}`;
          },
        },
        credits: {
          enabled: false,
        },
        accessibility: {
          enabled: false,
        },
      }) as Options,
    [
      categories,
      barData,
      lineData,
      title,
      subtitle,
      yAxisTitles.barAxisTitle,
      yAxisTitles.lineAxisTitle,
      max,
      tooltipFormatter,
    ]
  );

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default VerticalBarChart;
